import { useState, useEffect, useRef } from 'react';

import {
    faCaretDown,
    faCaretRight,
    faClipboard,
    faPhone,
    faSpinner,
    faCancel,
    faSms,
    faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { AjaxResponse } from 'rxjs/ajax';

import { callTrackingService } from 'components/CareOnDemand/Scheduler/AppointmentForm/Subjects';

import { ajaxGet, ajaxPost } from '../../../../common/ajax';
import { notificationService } from '../../../../subjects/common/NotificationService';
import './MeetingDetails.css';
import { callSidDelimiter, hangUpOutgoingCall } from '../helpers';
import { TELEHEALTH_MODULES_TO_URL } from '../MyPanelHelpers';

const MeetingDetails = ({
    conferenceId,
    phoneNumber,
    appointmentId,
    memberId,
    memberConnectionId,
    memberFullName,
    memberPhone,
    optionalAttendees,
    participants,
    telehealthModuleType,
}) => {
    const [showDetails, setShowDetails] = useState(true);
    const [showLinks, setShowLinks] = useState(false);
    const [telehealthLink, setTelehealthLink] = useState(null);
    const [optionalParticipants, setOptionalParticipants] = useState<{
        connectionId: string;
        fullName: string;
        relationship: string;
        phone: string;
        email: string;
        callSid?: string;
        isCalling?: boolean;
        callStatus?: string;
    }[]>(optionalAttendees);
    const [memberOutgoingCallDetails, setMemberOutgoingCallDetails] = useState<{
        callSid?: string; callStatus?: string; isCalling?: boolean;
    }>({});

    const telehealthSubscription: any = useRef(null);
    const memberTooltipRef: any = useRef(null);
    const optionalTooltipRefs: any = useRef({});
    const resendSMSSubscription: any = useRef(null);
    const resendEmailSubscription: any = useRef(null);
    const placeOutboundCallSubscription: any = useRef(null);
    const hangUpOutboundCallSubscription: any = useRef(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const callTimeouts = useRef<{ [callSid: string]: NodeJS.Timeout }>({});
    const CALL_TIMEOUT_MS = 30000;

    useEffect(() => {
        const ringtoneUrl = '/sound/us_ringback.mp3';
        audioRef.current = new Audio(ringtoneUrl);
        audioRef.current.loop = true;
        audioRef.current.volume = 0.25;
    }, []);

    const playRingtone = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch((err) => {
                console.warn("Ringtone autoplay blocked:", err);
            });
        }
    };

    const stopRingtone = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    };

    // cleanup
    useEffect(() => {
        return () => {
            telehealthSubscription.current?.unsubscribe?.();
            resendSMSSubscription.current?.unsubscribe?.();
            resendEmailSubscription.current?.unsubscribe?.();
            placeOutboundCallSubscription.current?.unsubscribe?.();
            hangUpOutboundCallSubscription.current?.unsubscribe?.();


            // Hang up all active calls for cleanup (also clears timeouts)
            Object.keys(callTimeouts.current).forEach(callSid => {
                handleHangUpOutgoingCall(callSid);
            });
            callTimeouts.current = {};

            stopRingtone();
        };
    }, []);

    useEffect(() => {
        if (!appointmentId) return;

        callTrackingService.start(appointmentId);

        const sub = callTrackingService.getUpdates$().subscribe(update => {
            const isCalling = ["queued", "initiated", "ringing", "in-progress"].includes(
                update.status || "");

            // Clear timeout for this callSid if exists
            if (update.callSid && callTimeouts.current[update.callSid]) {
                clearTimeout(callTimeouts.current[update.callSid]);
                delete callTimeouts.current[update.callSid];
            }

            if (["in-progress", "completed", "failed", "busy", "canceled"].includes(update.status)) {
                stopRingtone();
            }

            updateOptionalParticipant(
                update.callSid,
                update.status,
                isCalling
              );

            setMemberOutgoingCallDetails(prevState => {
                if (prevState.callSid === update.callSid) {
                  return { ...prevState, callStatus: update.status, isCalling: isCalling };
                }
                return prevState;
              });
        });

        return () => {
            sub.unsubscribe();
            callTrackingService.stop(appointmentId);
        };
    }, [appointmentId]);

    useEffect(() => {
        if (!participants || !Array.isArray(participants)) return;

        setOptionalParticipants(prevOptionals => {
            const updated = prevOptionals.map(opt => {
                const updatedParticipant = participants.find(p => p.identity.startsWith(opt.fullName));
                if (updatedParticipant) {
                    // Clear timeout for this participant if exists
                    if (opt.callSid && callTimeouts.current[opt.callSid]) {
                        clearTimeout(callTimeouts.current[opt.callSid]);
                        delete callTimeouts.current[opt.callSid];
                    }
                    stopRingtone();
                    return { ...opt, callStatus: 'connected' };
                } else {
                    // Participant is missing, mark as disconnected
                    return { ...opt, callStatus: 'disconnected', isCalling: false, callSid: undefined };
                }
            });
            return updated;
        });

        // Also check for member (main attendee)
        if (memberOutgoingCallDetails && memberOutgoingCallDetails.isCalling && participants) {
            const member = participants.find(p =>
                p.identity.startsWith(memberFullName) &&
                p.identity.includes(callSidDelimiter)
            );
            if (member) {
                // Clear timeout for member if exists
                if (memberOutgoingCallDetails.callSid && callTimeouts.current[memberOutgoingCallDetails.callSid]) {
                    clearTimeout(callTimeouts.current[memberOutgoingCallDetails.callSid]);
                    delete callTimeouts.current[memberOutgoingCallDetails.callSid];
                }
                stopRingtone();
                setMemberOutgoingCallDetails(prev => ({ ...prev, callStatus: 'connected' }));
            } else {
                // Member is missing, mark as disconnected
                setMemberOutgoingCallDetails(prev => ({ ...prev, callStatus: 'disconnected', isCalling: false, callSid: undefined }));
            }
        }
    }, [participants]);

    const handleHover = (isMember, connectionId : string | null = null) => {
        if (isMember) {
            memberTooltipRef.current.content = 'Copy invitation link';
        } else if (connectionId && optionalTooltipRefs.current[connectionId]) {
            optionalTooltipRefs.current[connectionId].content = 'Copy invitation link';
        }
    };

    const getLink = (connectionId, isMember = false) => {
        if (telehealthLink) {
            const link = `${telehealthLink}/${appointmentId}/${connectionId}`;
            navigator.clipboard.writeText(link || '');
        } else {
            telehealthSubscription.current = ajaxGet(
                `/api/${TELEHEALTH_MODULES_TO_URL[telehealthModuleType]}/TelehealthLink`,
            ).subscribe({
                next: (response) => {
                    if (response.response) {
                        window.focus();
                        const link = `${response.response}/${appointmentId}/${connectionId}`;
                        navigator.clipboard.writeText(link || '');
                        setTelehealthLink((response as any).response);
                    }
                },
                error: console.error,
            });
        }

        // change tooltip text
        if (isMember && memberTooltipRef.current) {
            memberTooltipRef.current.content = 'Text is copied!';
        } else if (optionalTooltipRefs.current[connectionId]) {
            optionalTooltipRefs.current[connectionId].content = 'Text is copied!';
        }
    };

    const outboundCall = (connectionId, isMember, phoneNumber, telehealthModuleType) => {
        playRingtone();

        placeOutboundCallSubscription.current = ajaxPost(
            `api/${TELEHEALTH_MODULES_TO_URL[telehealthModuleType]}/PlaceOutboundCall?appointmentId=${appointmentId}&memberId=${memberId}&connectionId=${connectionId}`,
        ).subscribe({
            next: (response: AjaxResponse<{ sid: string }>) => {
                if (response?.response?.sid) {
                    const callSid = response.response.sid;
                    // Start timeout for this callSid
                    if (callTimeouts.current[callSid]) {
                        clearTimeout(callTimeouts.current[callSid]);
                    }
                    callTimeouts.current[callSid] = setTimeout(() => {
                        stopRingtone();
                        // Set isCalling to false for member or participant
                        if (isMember) {
                            setMemberOutgoingCallDetails(prevState => {
                                if (prevState.callSid === callSid) {
                                    return { ...prevState, isCalling: false };
                                }
                                return prevState;
                            });
                        } else {
                            setOptionalParticipants(prev =>
                                prev.map(participant =>
                                    participant.callSid === callSid
                                        ? { ...participant, isCalling: false }
                                        : participant
                                )
                            );
                        }
                        // Remove timeout reference
                        delete callTimeouts.current[callSid];
                    }, CALL_TIMEOUT_MS);

                    if(isMember){
                        setMemberOutgoingCallDetails(prevState =>
                            ({...prevState, callSid: callSid, isCalling: true}));
                    } else {
                        setOptionalParticipants(prev =>
                            prev.map(participant =>
                                participant.connectionId === connectionId
                                ? { ...participant, callSid: callSid, isCalling: true }
                                : participant
                            )
                        );
                    }
                }
            },
            error: (err) => {
                stopRingtone();
                setMemberOutgoingCallDetails(prevState => ({...prevState, isCalling: false}));

                setOptionalParticipants(prev =>
                    prev.map(participant =>
                        participant.connectionId === connectionId
                        ? { ...participant, isCalling: false }
                        : participant
                    )
                );
                notificationService.error({
                    title: 'Failed to initiate call!',
                    content: err ? err.response?.error : `Invalid phone number <br /> ${phoneNumber}`,
                });
            },
        });
    };

    const onSendSMSClick = (args, memberId, appointmentId, telehealthModuleType) => {
        return ajaxGet(
            `api/${TELEHEALTH_MODULES_TO_URL[telehealthModuleType]}/SendSMS?appointmentId=${appointmentId}&memberId=${memberId}&receiverNumber=${args.phone}&connectionId=${args.connectionId}&moduleType=${telehealthModuleType}`,
        );
    };

    const handleSendSMS = (args, memberId, appointmentId, telehealthModuleType) => {
        resendSMSSubscription.current = onSendSMSClick(args, memberId, appointmentId, telehealthModuleType).subscribe({
            next: (response) => {
                if (response.status === 200) {
                    notificationService.success({ title: `SMS has been sent to ${args.phone}.` });
                }
            },
            error: () => {
                notificationService.error({ title: 'Failed to send SMS. Please try later.' });
            },
        });
    };

    const onResendInvite = (payload) => {
        return ajaxPost(`api/${TELEHEALTH_MODULES_TO_URL[payload.moduleType]}/ResendInvite`, payload);
    };

    const handleSendEmail = (props) => {
        const payload = {
            memberId: props.memberId,
            id: props.appointmentId,
            receiverConnectionId: props.connectionId,
            isPatient: props.isPatient,
            moduleType: props.telehealthModuleType,
        };

        resendEmailSubscription.current = onResendInvite(payload).subscribe({
            next: (response) => {
                if (response.status === 200) {
                    notificationService.success({
                        title: 'The appointment invite has been resent successfully',
                    });
                } else {
                    notificationService.error({
                        title: 'Failed to resend appointment invite, please try again later.',
                    });
                }
            },
            error: () => {
                notificationService.error({
                    title: 'Failed to resend appointment invite, please try again later.',
                });
            },
        });
    };

    const handleHangUpOutgoingCall = (callSid) => {
        if (!callSid){
            notificationService.warning({
                title: 'No active call to hang up!',
            });
            return;
        }

        // Clear timeout for this callSid if exists
        if (callTimeouts.current[callSid]) {
            clearTimeout(callTimeouts.current[callSid]);
            delete callTimeouts.current[callSid];
        }

        hangUpOutboundCallSubscription.current = hangUpOutgoingCall(telehealthModuleType, callSid).subscribe({
            next: (response) => {
                if (response.status === 200) {
                    notificationService.success({
                        title: 'The outgoing call has been hung up successfully',
                    });
                } else {
                    notificationService.error({
                        title: 'Failed to hang up the outgoing call, please try again later.',
                    });
                }
                stopRingtone();

                if(callSid === memberOutgoingCallDetails.callSid){
                    setMemberOutgoingCallDetails(prevState => ({...prevState, isCalling: false, callSid: undefined}));
                }
                else {
                    setOptionalParticipants(prev =>
                        prev.map(participant =>
                            participant.callSid === callSid
                                ? { ...participant, isCalling: false, callSid: undefined }
                                : participant
                        )
                    );
                }
            },
            error: () => {
                stopRingtone();
                notificationService.error({
                    title: 'Failed to hang up the outgoing call, please try again later.',
                });
            },
        });
    };

    const updateOptionalParticipant = (callSid: string, status: string, isCalling: boolean) => {
        setOptionalParticipants(prev =>
            prev.map(participant =>
            participant.callSid === callSid
                ? { ...participant, callStatus: status, isCalling }
                : participant
            )
        );
    };

    return (
        <div className='meeting-details'>
            <div
                className='meeting-details-title'
                onClick={() => setShowDetails((prevState) => !prevState)}
            >
                <span>Meeting Details</span>
                <span className='caret-info'>
                    <FontAwesomeIcon
                        icon={showDetails ? faCaretDown : faCaretRight}
                        size='lg'
                    />
                </span>
            </div>
            {showDetails && (
                <div className='meeting-details-content'>
                    <div>Dial in manually: {phoneNumber}</div>
                    <div>Conference Id: {conferenceId}</div>
                    <div
                        className='meeting-details-links-title'
                        onClick={() => setShowLinks((prevState) => !prevState)}
                    >
                        <span>Appointment links</span>
                        <span className='caret-info'>
                            <FontAwesomeIcon
                                icon={showLinks ? faCaretDown : faCaretRight}
                                size='lg'
                            />
                        </span>
                    </div>
                    {showLinks && (
                        <div className='appointment-invitation-links'>
                            <div className='member-invitation-links'>
                                <span>{memberFullName}</span>
                                <div
                                    className='caret-info virtual-visits-meeting-icon'
                                    onClick={() => getLink(memberConnectionId, true)}
                                    onMouseEnter={() => handleHover(true)}
                                >
                                    <TooltipComponent
                                        ref={memberTooltipRef}
                                        content='Copy invitation link'
                                        className='virtual-visits-meeting-icon'
                                        position='TopCenter'
                                    >
                                        <FontAwesomeIcon
                                            icon={faClipboard}
                                            size='xl'
                                        />
                                    </TooltipComponent>
                                </div>
                                {memberPhone && (
                                    <>
                                        <div
                                            className='caret-info phone-attendee virtual-visits-meeting-icon'
                                            onClick={() => {
                                                handleHangUpOutgoingCall(memberOutgoingCallDetails.callSid);
                                            }}
                                        >
                                            <TooltipComponent
                                                className='virtual-visits-meeting-icon'
                                                content='Hang up call'
                                                position='TopCenter'
                                            >
                                                <FontAwesomeIcon
                                                    icon={faCancel}
                                                    className="text-blue-500 text-xl"
                                                    size='xl'
                                                />
                                            </TooltipComponent>
                                        </div>
                                        {(memberOutgoingCallDetails.callSid &&
                                            memberOutgoingCallDetails.isCalling) ? (
                                            <div
                                                className='caret-info phone-attendee virtual-visits-meeting-icon'
                                            >
                                                <TooltipComponent
                                                    className='virtual-visits-meeting-icon'
                                                    content='Call in progress'
                                                    position='TopCenter'
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faSpinner}
                                                        spin={true}
                                                        className="text-blue-500 text-xl"
                                                        size='xl'
                                                    />
                                                </TooltipComponent>
                                            </div>
                                        ) : (
                                            <div
                                                className='caret-info phone-attendee virtual-visits-meeting-icon'
                                                onClick={() => {
                                                        outboundCall(
                                                            memberConnectionId,
                                                            true,
                                                            memberPhone,
                                                            telehealthModuleType,
                                                        )
                                                }}
                                            >
                                                <TooltipComponent
                                                    className='virtual-visits-meeting-icon'
                                                    content='Call attendee'
                                                    position='TopCenter'
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faPhone}
                                                        size='xl'
                                                    />
                                                </TooltipComponent>
                                            </div>
                                        )}
                                        <div className='caret-info sms-attendee'>
                                            <TooltipComponent
                                                content='Resend invitation SMS'
                                                position='TopCenter'
                                                className='virtual-visits-meeting-icon'
                                            >
                                                <FontAwesomeIcon
                                                    title='Resend invitation SMS'
                                                    icon={faSms}
                                                    size='xl'
                                                    onClick={() =>
                                                        handleSendSMS(
                                                            {
                                                                phone: memberPhone,
                                                                connectionId: memberConnectionId,
                                                            },
                                                            memberId,
                                                            appointmentId,
                                                            telehealthModuleType,
                                                        )
                                                    }
                                                />
                                            </TooltipComponent>
                                        </div>
                                    </>
                                )}
                                <div className='caret-info email-attendee'>
                                    <TooltipComponent
                                        content='Resend invitation email'
                                        position='TopCenter'
                                        className='virtual-visits-meeting-icon'
                                    >
                                        <FontAwesomeIcon
                                            title='Resend invitation email'
                                            icon={faEnvelope}
                                            size='xl'
                                            onClick={() =>
                                                handleSendEmail({
                                                    appointmentId,
                                                    memberId,
                                                    connectionId: memberConnectionId,
                                                    isPatient: true,
                                                    telehealthModuleType,
                                                })
                                            }
                                        />
                                    </TooltipComponent>
                                </div>
                            </div>
                            {optionalParticipants &&
                                optionalParticipants.length > 0 &&
                                optionalParticipants
                                    .filter((optional) => optional.relationship != 'Admin')
                                    .map((attendee, index) => {
                                        return (
                                            <div key={`optional-attendee-${index}`}>
                                                <span>{attendee.fullName}</span>
                                                <div
                                                    className='caret-info'
                                                    onClick={() => getLink(attendee.connectionId, false)}
                                                    onMouseEnter={() => handleHover(false, attendee.connectionId)}
                                                >
                                                    <TooltipComponent
                                                        ref={(el) =>
                                                            (optionalTooltipRefs.current[attendee.connectionId] = el)
                                                        }
                                                        content='Copy invitation link'
                                                        className='virtual-visits-meeting-icon'
                                                        position='TopCenter'
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faClipboard}
                                                            size='xl'
                                                        />
                                                    </TooltipComponent>
                                                </div>
                                                {attendee.phone && (
                                                    <>
                                                        <div
                                                            className='caret-info phone-attendee'
                                                            onClick={() => {
                                                                handleHangUpOutgoingCall(attendee.callSid);
                                                            }}
                                                        >
                                                            <TooltipComponent
                                                                className='virtual-visits-meeting-icon'
                                                                content='Hang up call'
                                                                position='TopCenter'
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faCancel}
                                                                    className='text-blue-500 text-xl'
                                                                    size='xl'
                                                                />
                                                            </TooltipComponent>
                                                        </div>
                                                        {(attendee.callSid && attendee.isCalling) ? (
                                                            <>
                                                                <div
                                                                    className='caret-info phone-attendee'
                                                                >
                                                                    <TooltipComponent
                                                                        className='virtual-visits-meeting-icon'
                                                                        content='Call in progress'
                                                                        position='TopCenter'
                                                                    >
                                                                        <FontAwesomeIcon
                                                                            icon={faSpinner}
                                                                            spin={true}
                                                                            className='text-blue-500 text-xl'
                                                                            size='xl'
                                                                        />
                                                                    </TooltipComponent>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <span
                                                                className='caret-info phone-attendee'
                                                                onClick={() => {
                                                                    outboundCall(
                                                                        attendee.connectionId,
                                                                        false,
                                                                        attendee.phone,
                                                                        telehealthModuleType
                                                                    );
                                                                }}
                                                            >
                                                                <TooltipComponent
                                                                    content='Call attendee'
                                                                    position='TopCenter'
                                                                    className='virtual-visits-meeting-icon'
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faPhone}
                                                                        size='xl'
                                                                    />
                                                                </TooltipComponent>
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MeetingDetails;
